import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, Link, useParams } from 'react-router-dom'
import _ from 'lodash'
import { toast } from 'react-hot-toast'
import { v4 } from 'uuid'

import api from 'Src/api'

import ToggleSwitch from 'Src/components/Toggle'
import Tags from 'Src/components/TagsInput'
import { Prompt } from 'Src/history'
import backArrow from '../assets/images/back-arrow.svg?url'

import TrixEditor from '../components/Trix'

import { fetchSpecificGuide } from 'Actions/guides'
import { getSpecificGuide } from 'Selectors/guides'

const PATCH_GUIDE_URL = 'v1/guides/:guide_id/'
const CREATE_GUIDE_URL = 'v1/guides/'
const CREATE_GUIDE_STEP_URL = 'v1/guides/:guide_id/steps/'
const PATCH_GUIDE_STEP_URL = 'v1/guides/:guide_id/steps/:guide_step_id/'
const DELETE_GUIDE_STEP_URL = 'v1/guides/:guide_id/steps/:guide_step_id/'
const UPLOAD_IMAGE_URL = 'v1/guides/steps/upload/'

const toastId = v4()

const showErrorToast = (error, defaultMessage = 'Something went wrong. Try again') => {
  const data = _.get(error, 'response.data', {})
  let errorMsg = defaultMessage

  switch (data.type) {
    case 'GUIDE_ALREADY_EXISTS':
      errorMsg = data.message
      break

    default:
      errorMsg = defaultMessage
  }

  toast.error(errorMsg, { id: toastId })
}

const Guides = (props) => {
  const { isEditMode = false } = props

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()

  const isCreateMode = !isEditMode

  const [editStepNo, setEditStepNo] = useState(null)
  const [allowAdd, toggleAllowAdd] = useState(true)
  const [guideName, setGuideName] = useState('')
  const [guideActiveState, setGuideActiveState] = useState(false)
  const [feedback, setFeedback] = useState(false)
  const [guideTags, setGuideTags] = useState([])
  const [currentGuideStepId, setCurrentGuideStepId] = useState(null)
  const [stepContent, setStepContent] = useState([
    {
      content: ''
    }
  ])
  const [uploadingImage, setUploadingImage] = useState(false)
  const [nameErrorMessage, setNameErrorMessage] = useState('')

  const disableGuideTags = isCreateMode
  const contentOrTitleMissing = !(stepContent[editStepNo]?.content && guideName)
  const disableSaveButton = contentOrTitleMissing || uploadingImage
  const disableDeleteButton = stepContent.length < 2 || !stepContent[editStepNo]?.content || currentGuideStepId == null

  useEffect(() => {
    if (isCreateMode) {
      // Clear the state user navigates to create guide
      setEditStepNo(null)
      setGuideName('')
      setGuideActiveState(false)
      setFeedback(false)
      setGuideTags([])
      setStepContent([
        {
          content: ''
        }
      ])
    }
  }, [isCreateMode])

  // used in editMode
  const guideId = params.id

  /**
   * When page is refreshed, guide in store is reset to empty.
   * We need to fetch the guide again.
   */
  useEffect(() => {
    if (isEditMode) {
      dispatch(fetchSpecificGuide({ guideId }))
    }
  }, [dispatch, isEditMode, guideId])

  const guide = useSelector(getSpecificGuide)

  useEffect(() => {
    if (!isEditMode || _.isEmpty(guide)) {
      return
    }

    const name = _.get(guide, 'name')
    const guideSteps = _.get(guide, 'guide_steps')
    const guideState = _.get(guide, 'is_active')
    const guideFeedback = _.get(guide, 'feedback')
    const guideTags = _.get(guide, 'tags')
    const filterGuideStep = guideSteps.map((guideStep) => {
      return {
        id: guideStep.id,
        content: guideStep.content
      }
    })

    setEditStepNo(filterGuideStep.length)
    setGuideName(name)
    setGuideActiveState(guideState)
    setFeedback(guideFeedback.enabled)
    setGuideTags(guideTags)
    setStepContent(filterGuideStep)
  }, [guide, isEditMode])

  useEffect(() => {
    if (isCreateMode) {
      toggleAllowAdd(false)
      setEditStepNo(0)
    }
  }, [isCreateMode])

  const onGuideNameChange = (event) => {
    const { value } = event.target
    const name = _.get(guide, 'name')
    setGuideName(value)
    if (value.length < 3) {
      setNameErrorMessage('Title must contain atleast 3 characters')
      return
    }
    setNameErrorMessage('')
    if (!isCreateMode && event.type === 'blur' && guideName !== name) {
      handleChange('guideName', guideName)
    }
  }

  const handleChange = (property, value) => {
    if (!isCreateMode) {
      const payload = {
        guideName,
        guideActiveState,
        feedback,
        guideTags
      }
      payload[property] = value
      handleUpdateGuide(payload)
      if (property === 'feedback') setFeedback(value)
      else if (property === 'guideTags') setGuideTags(value)
      else if (property === 'guideActiveState') setGuideActiveState(value)
    }
  }

  const handleDeleteGuideStep = async () => {
    const canDelete = confirm('Are you sure you want to delete this step?')
    if (!canDelete) return
    try {
      await api.delete(DELETE_GUIDE_STEP_URL, { urlParams: { guide_id: guideId, guide_step_id: currentGuideStepId } })
      setEditStepNo(stepContent.length - 1)
      toggleAllowAdd(true)
      setCurrentGuideStepId(null)
      dispatch(fetchSpecificGuide({ guideId }))
      toast.success('Deleted Successfully', { id: toastId })
    } catch (error) {
      console.error('Error while deleting guide steps', error)
      toast.error('Error while deleting step', { id: toastId })
    }
  }

  const handleClear = () => {
    toggleAllowAdd(true)
    setCurrentGuideStepId(null)
    dispatch(fetchSpecificGuide({ guideId }))
  }

  const handleUploadImage = async (event) => {
    const attachment = event.attachment
    if (attachment.file) {
      try {
        setUploadingImage(true) // Disable Save button while image is being uploaded
        const formData = new FormData()
        formData.append('file', attachment.file)
        const response = await api.post(UPLOAD_IMAGE_URL, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        const { fileUrl } = response.data
        attachment.setAttributes({
          url: fileUrl,
          href: fileUrl
        })
      } catch (error) {
        console.error('Error while uploading image to server', error)
      } finally {
        setUploadingImage(false) // Enable Save button in case of error or success of API call
      }
    }
  }

  const editContent = (index, currentStepId) => {
    setEditStepNo(index)
    setCurrentGuideStepId(currentStepId)
    toggleAllowAdd(false)
  }

  const handleSave = () => {
    setEditStepNo(null)
    toggleAllowAdd(true)
    if (currentGuideStepId) {
      const value = stepContent.find((step) => step.id === currentGuideStepId).content
      patchGuideStep(currentGuideStepId, value)
    } else {
      createGuideStep(stepContent[stepContent.length - 1].content)
    }
    setCurrentGuideStepId(null)
  }

  const handleUpdateGuide = async (payload) => {
    const { guideName, guideActiveState, feedback, guideTags } = payload
    const reqPayload = {
      name: guideName,
      is_active: guideActiveState,
      feedback: {
        enabled: feedback,
        settings: {}
      },
      tags: guideTags
    }

    try {
      await api.patch(PATCH_GUIDE_URL, reqPayload, { urlParams: { guide_id: guideId } })
      dispatch(fetchSpecificGuide({ guideId }))
      toast.success('Updated Successfully', { id: toastId })
    } catch (error) {
      console.error('Error while updating guide', error)

      const data = _.get(error, 'response.data', {})

      switch (data.type) {
        case 'GUIDE_ALREADY_EXISTS':
          setNameErrorMessage(data.message)
          break
      }

      showErrorToast(error, 'Error while updating guide')
    }
  }

  const createGuideStep = async (stepContent) => {
    const payload = {
      guide_id: guideId,
      name: guideName,
      is_active: guideActiveState,
      feedback: {
        enabled: feedback,
        settings: {}
      },
      content: stepContent,
      tags: guideTags
    }

    try {
      let guide_id = guideId
      if (isCreateMode) {
        const { data } = await api.post(CREATE_GUIDE_URL, payload)
        guide_id = data.id
        navigate(`/guides/edit/${guide_id}`, { replace: true })
      } else {
        await api.post(CREATE_GUIDE_STEP_URL, payload, { urlParams: { guide_id } })
      }
      toast.success('Created Successfully', { id: toastId })
      dispatch(fetchSpecificGuide({ guideId: guide_id }))
    } catch (error) {
      console.error('Error while creating guide step', error)
      const data = _.get(error, 'response.data', {})

      switch (data.type) {
        case 'GUIDE_ALREADY_EXISTS':
          setNameErrorMessage(data.message)
          break
      }

      showErrorToast(error, 'Error while creating guide')
    }
  }

  const patchGuideStep = async (guideStepId, stepContent) => {
    const payload = {
      content: stepContent
    }

    try {
      await api.patch(PATCH_GUIDE_STEP_URL, payload, { urlParams: { guide_id: guideId, guide_step_id: guideStepId } })
      toast.success('Updated Successfully', { id: toastId })
      dispatch(fetchSpecificGuide({ guideId }))
    } catch (error) {
      console.error('Error while updating guide steps', error)
      toast.error('Error while updating guide step', { id: toastId })
    }
  }

  const handleContentChange = (content) => {
    const newStepContent = [...stepContent]
    if (!currentGuideStepId) {
      newStepContent[newStepContent.length - 1] = { content }
    } else {
      const index = newStepContent.findIndex((step) => step.id === currentGuideStepId)
      newStepContent[index].content = content
    }
    setStepContent(newStepContent)
  }

  const steps = stepContent.map((step, index) => {
    if (editStepNo === index) {
      return (
        <div className='guide-step' key={index}>
          <div className='guide-step-index active'>
            <span>{index + 1}</span>
          </div>
          <div className='guide-step-content-editor'>
            <TrixEditor
              defaultValue={step.content}
              contentChangeHandler={handleContentChange}
              uploadImage={handleUploadImage}
            >
              editing step {index + 1}
            </TrixEditor>
            <div className='button-bar'>
              <button className='button-primary' disabled={disableSaveButton} onClick={handleSave}>
                Save
              </button>
              <button className='button-link' onClick={handleClear}>
                Cancel
              </button>
              <button className='button-link right' onClick={handleDeleteGuideStep} disabled={disableDeleteButton}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div className='guide-step' key={index}>
          <div className='guide-step-index'>
            <span>{index + 1}</span>
          </div>
          <div
            className='guide-step-content'
            dangerouslySetInnerHTML={createMarkup(step.content)}
            onClick={() => editContent(index, step.id)}
          />
        </div>
      )
    }
  })

  function createMarkup(content) {
    return { __html: content }
  }

  function addStep() {
    const newStepContent = [...stepContent]
    newStepContent.push({ content: '' })
    setStepContent(newStepContent)
    setEditStepNo(newStepContent.length - 1)
    toggleAllowAdd(false)
  }

  return (
    <div className='page-container guides'>
      <div className='main-content'>
        <div className='page-header'>
          <Link className='back-button mr-3' to='/guides'>
            <img src={backArrow} />
          </Link>

          <div className='section-heading'>
            <div className='section-name'>GUIDE</div>
            <div className='section-title'>
              <input
                placeholder='Enter Guide title'
                name='name'
                value={guideName}
                onChange={onGuideNameChange}
                onBlur={onGuideNameChange}
              />
              {nameErrorMessage && <small className='text-danger'>{nameErrorMessage}</small>}
            </div>
          </div>
          <div className='section-actions'>
            {/* TODO: implement preview,clone,delete  */}
            <button style={{ visibility: 'hidden' }} className='button-secondary'>
              Preview
            </button>
            <button style={{ visibility: 'hidden' }} className='button-more' />
          </div>
        </div>
        <div>{steps}</div>
        {allowAdd && (
          <div className='add-guide-step' disabled={!allowAdd} onClick={() => addStep()}>
            <div className='add-icon' />
            <div className='add-label'>Add New Guide Step</div>
          </div>
        )}
      </div>
      <div className='guides-sidebar-container'>
        <div className='settings-heading'>Manage</div>
        <div className='settings-row clearfix'>
          <div className='settings-label'>Active</div>
          <div className='settings-switch'>
            <ToggleSwitch
              checked={guideActiveState}
              onClick={() => handleChange('guideActiveState', !guideActiveState)}
            />
          </div>
        </div>
        {/* <div className='settings-row clearfix'>
          <div className='settings-label'>Collect Feedback</div>
          <div className='settings-switch'>
            <ToggleSwitch checked={feedback} onClick={() => handleChange('feedback', !feedback)} />
          </div>
        </div> */}

        <div className='settings-heading'>Tags</div>
        <Tags
          tags={guideTags}
          placeholder='Add a tag'
          disabled={disableGuideTags}
          onChange={(tags) => handleChange('guideTags', tags)}
        />
      </div>
      <Prompt when={!disableSaveButton} message='Are you sure you want to discard your unsaved changes?' />
    </div>
  )
}

export default Guides
